import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DrawerLink } from '../../app/DrawerLink';
import { Link } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';
import { label, type CreditRole } from '../tracks/CreditsEditor';
import { Verified } from '../../components/ui/Verified';

interface PendingCredit {
  id: number;
  role: CreditRole;
  created_at: string;
  track: {
    id: number;
    title: string;
    slug: string;
    status: string;
    artwork_small: string | null;
  };
  offered_by: {
    username: string;
    display_name: string | null;
    verified?: boolean;
  };
}

/**
 * Credits somebody has put your name on, awaiting your answer.
 *
 * The other half of the confirmation rule. A credit on a real account reaches
 * nobody until it is accepted here — not the credited person's profile, and not
 * their followers' feeds — which is what stops a credit being a way to put a
 * track in front of somebody else's audience.
 *
 * Renders nothing at all when there is nothing pending. A permanent empty
 * section is a section people learn to scroll past, which defeats the point of
 * putting it where they will see it.
 */
export function PendingCredits() {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['pending-credits'],
    queryFn: () => api.get<{ items: PendingCredit[] }>('/me/credits'),
  });

  const respond = useMutation({
    mutationFn: ({ id, accept }: { id: number; accept: boolean }) =>
      api.post(`/me/credits/${id}`, { accept }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['pending-credits'] }),
  });

  const items = data?.items ?? [];

  if (items.length === 0) return null;

  return (
    <section className="flex flex-col gap-3" data-pending-credits>
      <div>
        <h2 className="text-lg font-semibold text-ink">
          {items.length === 1 ? 'A credit is waiting for you' : `${items.length} credits are waiting for you`}
        </h2>
        <p className="mt-1 text-sm text-soft">
          Your name will not appear on these, and they will not reach your followers, until you
          accept.
        </p>
      </div>

      <ul className="flex flex-col gap-2">
        {items.map((credit) => (
          <li key={credit.id} data-pending-credit={credit.id}>
            <Card className="flex flex-wrap items-center gap-4 p-4">
              {credit.track.artwork_small ? (
                <img
                  src={credit.track.artwork_small}
                  alt=""
                  className="h-11 w-11 shrink-0 rounded-[var(--radius-control)] object-cover"
                />
              ) : (
                <div
                  aria-hidden
                  className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
                  style={{ backgroundImage: 'repeating-linear-gradient(45deg, var(--color-blank) 0 6px, var(--color-blank-alt) 6px 12px)' }}
                />
              )}

              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink">
                  <Link to={`/${credit.offered_by.username}`} className="font-medium hover:underline">
                    {credit.offered_by.display_name || `@${credit.offered_by.username}`}
                    {credit.offered_by?.verified && <Verified />}
                  </Link>{' '}
                  credited you as{' '}
                  <span className="font-medium">{label(credit.role).toLowerCase()}</span> on{' '}
                  <DrawerLink to={`/tracks/${credit.track.id}`} className="font-medium hover:underline">
                    {credit.track.title}
                  </DrawerLink>
                  .
                </p>
              </div>

              <div className="flex shrink-0 gap-2">
                <Button
                  className="px-4 py-1.5"
                  loading={respond.isPending && respond.variables?.id === credit.id}
                  onClick={() => respond.mutate({ id: credit.id, accept: true })}
                >
                  Accept
                </Button>
                <Button
                  variant="ghost"
                  className="px-4 py-1.5"
                  disabled={respond.isPending}
                  onClick={() => respond.mutate({ id: credit.id, accept: false })}
                >
                  Decline
                </Button>
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </section>
  );
}
